import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { usePermittedQuery } from '@/hooks/usePermittedQuery'
import {
  listRuns,
  getRun,
  preflightRun,
  createRun,
  calculateRun,
  submitRun,
  approveRun,
  rejectRun,
  voidRun,
  createAdjustment,
  listRunItems,
  getRunItem,
} from '@/api/services/payroll-runs.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  PayrollRunListFilters,
  CreatePayrollRunDto,
  CreatePayrollAdjustmentDto,
} from '@/features/payroll/types/payroll.types'

export const payrollRunKeys = {
  all: (companyId: string | null) => ['payroll-runs', companyId] as const,
  list: (companyId: string | null, filters?: PayrollRunListFilters) =>
    ['payroll-runs', companyId, 'list', filters] as const,
  detail: (companyId: string | null, runId: string | null) =>
    ['payroll-runs', companyId, 'detail', runId] as const,
  preflight: (companyId: string | null, runId: string | null) =>
    ['payroll-runs', companyId, 'preflight', runId] as const,
  items: (companyId: string | null, runId: string | null, page?: number) =>
    ['payroll-runs', companyId, 'items', runId, page] as const,
  item: (companyId: string | null, runId: string | null, itemId: string | null) =>
    ['payroll-runs', companyId, 'item', runId, itemId] as const,
}

async function invalidateRunViews(
  queryClient: ReturnType<typeof useQueryClient>,
  companyId: string,
  runId?: string,
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: payrollRunKeys.all(companyId) })
  // Batch readiness/consolidation reads per-company run statuses — keep it fresh.
  await queryClient.invalidateQueries({ queryKey: ['payroll-batches'] })
  if (!runId) return
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: payrollRunKeys.detail(companyId, runId) }),
    queryClient.invalidateQueries({ queryKey: payrollRunKeys.preflight(companyId, runId) }),
    queryClient.invalidateQueries({ queryKey: payrollRunKeys.items(companyId, runId) }),
  ])
}

// ─── Queries ───────────────────────────────────────

export function usePayrollRuns(
  companyId: string | null,
  filters?: PayrollRunListFilters,
  options?: { enabled?: boolean },
) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_company',
    queryKey: payrollRunKeys.list(companyId, filters),
    queryFn: () => listRuns(companyId!, filters),
    staleTime: 60_000,
    enabled: !!companyId && options?.enabled !== false,
  })
}

export function usePayrollRun(
  companyId: string | null,
  runId: string | null,
) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_company',
    queryKey: payrollRunKeys.detail(companyId, runId),
    queryFn: () => getRun(companyId!, runId!),
    staleTime: 60_000,
    enabled: !!companyId && !!runId,
  })
}

export function usePayrollRunPreflight(
  companyId: string | null,
  runId: string | null,
) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_company',
    queryKey: payrollRunKeys.preflight(companyId, runId),
    queryFn: () => preflightRun(companyId!, runId!),
    staleTime: 30_000,
    enabled: !!companyId && !!runId,
  })
}

export function usePayrollRunItems(
  companyId: string | null,
  runId: string | null,
  page = 1,
  limit = 20,
) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_company',
    queryKey: payrollRunKeys.items(companyId, runId, page),
    queryFn: () => listRunItems(companyId!, runId!, { page, limit }),
    staleTime: 60_000,
    enabled: !!companyId && !!runId,
  })
}

export function usePayrollRunItem(
  companyId: string | null,
  runId: string | null,
  itemId: string | null,
) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_company',
    queryKey: payrollRunKeys.item(companyId, runId, itemId),
    queryFn: () => getRunItem(companyId!, runId!, itemId!),
    staleTime: 60_000,
    enabled: !!companyId && !!runId && !!itemId,
  })
}

// ─── Mutations ─────────────────────────────────────

export function useCreateRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string; body: CreatePayrollRunDto }) =>
      createRun(companyId, body),
    onSuccess: async (_, { companyId }) => {
      await invalidateRunViews(qc, companyId)
      toast.success('Payroll run created.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useCalculateRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, runId }: { companyId: string; runId: string }) =>
      calculateRun(companyId, runId),
    onSuccess: async (_, { companyId, runId }) => {
      await invalidateRunViews(qc, companyId, runId)
      toast.success('Payroll calculated.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useSubmitRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, runId }: { companyId: string; runId: string }) =>
      submitRun(companyId, runId),
    onSuccess: async (_, { companyId, runId }) => {
      await invalidateRunViews(qc, companyId, runId)
      toast.success('Run submitted for approval.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useApproveRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, runId }: { companyId: string; runId: string }) =>
      approveRun(companyId, runId),
    onSuccess: async (_, { companyId, runId }) => {
      await invalidateRunViews(qc, companyId, runId)
      toast.success('Run approved.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useRejectRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      runId,
      reason,
    }: {
      companyId: string
      runId: string
      reason: string
    }) => rejectRun(companyId, runId, reason),
    onSuccess: async (_, { companyId, runId }) => {
      await invalidateRunViews(qc, companyId, runId)
      toast.success('Run rejected.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useVoidRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, runId }: { companyId: string; runId: string }) =>
      voidRun(companyId, runId),
    onSuccess: async (_, { companyId, runId }) => {
      await invalidateRunViews(qc, companyId, runId)
      toast.success('Run voided.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useCreateAdjustment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      runId,
      body,
    }: {
      companyId: string
      runId: string
      body: CreatePayrollAdjustmentDto
    }) => createAdjustment(companyId, runId, body),
    onSuccess: async (_, { companyId, runId }) => {
      await invalidateRunViews(qc, companyId, runId)
      toast.success('Adjustment run created.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
